// Settings → Roles (plan §10.3; B-080): each role's permissions, grouped by area in plain
// words, and who holds it. Default roles (Owner, Manager, Baker, Receiving, Front of house)
// can be changed but not deleted; a role that can take a connection live only an owner
// changes. Roles are account-wide, so only someone with all branches changes them.

import { brand } from '@enp/brand';
import type { Role } from '@enp/domain/api';
import { OWNER_PERMISSION } from '@enp/domain/permissions';
import { Badge, Button, Card, EmptyState, PageHeader, Skeleton } from '@enp/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Pencil, Plus, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { KeyHints, usePageKeys } from '../../../purchasing/parts.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { SettingsBreadcrumb, SettingsFrame, useAllBranches } from '../../../stations/page-parts.js';
import {
  NoStaffAccess,
  RoleDialog,
  useCanManageStaff,
  useMyPermissions,
} from '../../../staff/components.js';
import { rolesQuery } from '../../../staff/queries.js';
import { PERMISSION_GROUPS, PERMISSION_WORDS, permissionCount } from '../../../staff/words.js';

export const Route = createFileRoute('/_app/settings/roles')({
  component: RolesPage,
});

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function heldBy(role: Role): string {
  const parts = [
    role.staff_count > 0 ? plural(role.staff_count, 'person', 'people') : null,
    role.member_count > 0
      ? plural(
          role.member_count,
          `${brand.appNames.admin} member`,
          `${brand.appNames.admin} members`,
        )
      : null,
  ].filter(Boolean);
  return parts.length === 0 ? 'Nobody holds it' : `Held by ${parts.join(' and ')}`;
}

function RoleCard({
  role,
  canEdit,
}: {
  role: Role;
  /** null: can edit; else why not. */
  canEdit: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const headingId = `role-${role.id}`;
  return (
    <li>
      <Card className="gap-3 p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-col gap-1">
            <h2
              id={headingId}
              className="flex flex-wrap items-center gap-2 font-display text-lg font-semibold text-fg"
            >
              {role.name}
              {role.is_default && <Badge tone="neutral">Default</Badge>}
              {role.permissions.includes(OWNER_PERMISSION) && <Badge tone="primary">Owner</Badge>}
            </h2>
            {role.description && <p className="text-body text-muted">{role.description}</p>}
            <p className="text-sm text-fg">
              {permissionCount(role.permissions.length)} · {heldBy(role)}
            </p>
          </div>
          {canEdit === null ? (
            <RoleDialog
              role={role}
              open={editing}
              onOpenChange={setEditing}
              trigger={
                <Button
                  variant="secondary"
                  icon={<Pencil aria-hidden />}
                  aria-label={`Edit ${role.name}`}
                >
                  Edit
                </Button>
              }
            />
          ) : (
            <p className="max-w-56 text-sm text-muted sm:text-right">{canEdit}</p>
          )}
        </div>
        <details className="text-sm">
          <summary className="w-fit cursor-pointer rounded-sm font-medium text-fg">
            What it allows
          </summary>
          {role.permissions.length === 0 ? (
            <p className="mt-2 text-muted">Nothing yet: someone with only this role can’t act.</p>
          ) : (
            <dl className="mt-3 grid gap-x-8 gap-y-3 md:grid-cols-2">
              {PERMISSION_GROUPS.map((g) => {
                const keys = g.keys.filter((k) => role.permissions.includes(k));
                if (keys.length === 0) return null;
                return (
                  <div key={g.area} className="flex flex-col gap-1">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
                      {g.area}
                    </dt>
                    {keys.map((k) => (
                      <dd key={k} className="text-fg">
                        {PERMISSION_WORDS[k].label}
                      </dd>
                    ))}
                  </div>
                );
              })}
            </dl>
          )}
        </details>
      </Card>
    </li>
  );
}

function RolesSkeleton() {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-3">
      <span className="sr-only">Loading roles…</span>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex flex-col gap-2 rounded-lg border border-border bg-surface-raised p-5"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-72 max-w-full" />
              <Skeleton className="h-4 w-48" />
            </div>
            <Skeleton className="h-control w-20" />
          </div>
          <Skeleton className="h-4 w-28" />
        </div>
      ))}
    </div>
  );
}

function RolesPage() {
  useDocumentTitle('Roles');
  const canManage = useCanManageStaff();
  const allBranches = useAllBranches();
  const mine = useMyPermissions();
  const list = useQuery({ ...rolesQuery(), enabled: canManage === true });
  const [adding, setAdding] = useState(false);
  const owner = mine.includes(OWNER_PERMISSION);
  const canAdd = canManage === true && allBranches && list.data !== undefined;
  // "n" adds a role, as "n" starts something new on every other Admin list (UI8C-7).
  usePageKeys({ n: canAdd ? () => setAdding(true) : undefined });

  const whyNot = (role: Role): string | null => {
    if (!allBranches) return 'Roles apply to every branch: someone with all branches changes them.';
    if (role.permissions.includes(OWNER_PERMISSION) && !owner) {
      return 'Only an owner changes this role.';
    }
    return null;
  };

  return (
    <SettingsFrame>
      <SettingsBreadcrumb page="Roles" />
      <PageHeader
        title="Roles"
        description="A role is a set of permissions, like Baker or Manager. Give roles to people on the Staff page, for one branch or all."
        actions={
          canManage && allBranches && list.data ? (
            <RoleDialog
              open={adding}
              onOpenChange={setAdding}
              trigger={<Button icon={<Plus aria-hidden />}>Add role</Button>}
            />
          ) : undefined
        }
      />
      {canManage === false ? (
        <NoStaffAccess />
      ) : list.isError ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The roles"
          onRetry={() => void list.refetch()}
        />
      ) : !list.data ? (
        <RolesSkeleton />
      ) : list.data.roles.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="No roles"
          description="Add a role for each kind of job, then give it to people on the Staff page."
        />
      ) : (
        <>
          {canAdd && (
            <div className="mb-3 flex justify-end">
              <KeyHints keys={[['n', 'add role']]} />
            </div>
          )}
          <ul aria-label="Roles" className="flex flex-col gap-3">
            {list.data.roles.map((r) => (
              <RoleCard key={r.id} role={r} canEdit={whyNot(r)} />
            ))}
          </ul>
          <p className="mt-4 text-sm text-muted">
            People and their roles are on the{' '}
            <Link to="/settings/staff" className="font-medium text-fg underline">
              Staff
            </Link>{' '}
            page. Changes reach the stations at their next sync.
          </p>
        </>
      )}
    </SettingsFrame>
  );
}
